import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Phone, Mic, HeartHandshake, Bell } from 'lucide-react';

export const SeniorsPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const menuItems = [
        { label: 'Features', href: '/#features' },
        { label: 'Blog', href: '/blog' },
        { label: 'Press', href: '/press' },
        { label: 'Pitch', href: '/pitch' },
        { label: 'Enterprise API', href: '/enterprise-api' }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>Cura for Seniors – Simple Voice-First Health Guidance</title>
                <meta name="description" content="Get instant medical answers by phone or voice. No apps, no passwords – just compassionate health guidance designed for seniors, 24/7 ✓" />
                <link rel="canonical" href="https://mycura.org/seniors" />
                <meta property="og:title" content="Cura for Seniors – Simple Voice-First Health Guidance" />
                <meta property="og:description" content="Get instant medical answers by phone or voice. No apps, no passwords – just compassionate health guidance designed for seniors, 24/7." />
                <meta property="og:url" content="https://mycura.org/seniors" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Cura for Seniors – Simple Voice-First Health Guidance" />
                <meta name="twitter:description" content="Get instant medical answers by phone or voice. No apps, no passwords – just compassionate health guidance designed for seniors, 24/7." />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Cura for Seniors",
                        "url": "https://mycura.org/seniors",
                        "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" },
                            { "@type": "ListItem", "position": 2, "name": "For Seniors", "item": "https://mycura.org/seniors" }
                        ]
                    })}
                </script>
            </Helmet>
            <Navigation
                menuItems={menuItems}
                scrollToSection={(id) => {
                    const element = document.getElementById(id);
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                scrollToTop={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            <main className="pt-32 pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Hero */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
                        <div>
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-6xl md:text-8xl font-['Instrument_Serif'] text-slate-900 mb-8 leading-[1]"
                            >
                                Simple health <br />
                                <span className="italic text-[#003b82]">guidance.</span>
                            </motion.h1>
                            <p className="text-xl text-slate-500 leading-relaxed mb-8">
                                No complicated apps or passwords. Just talk to Cura like you would a friend, and get instant medical answers.
                            </p>
                            <button
                                onClick={() => window.open('tel:+18665204146', '_blank')}
                                className="inline-flex items-center px-8 py-4 rounded-full bg-[#003b82] text-white font-bold hover:bg-[#002d62] transition-colors"
                            >
                                <Phone className="w-5 h-5 mr-2" />
                                Call 1-866-520-4146
                            </button>
                        </div>
                        <div className="relative rounded-[3rem] overflow-hidden bg-slate-100 aspect-square">
                            <img
                                src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?auto=format&fit=crop&q=80&w=2070"
                                alt="Senior using phone"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>

                    {/* Features */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
                        <div className="p-8 rounded-3xl bg-[#F6F8FC]">
                            <Mic className="w-10 h-10 text-[#003b82] mb-6" />
                            <h3 className="text-3xl font-['Instrument_Serif'] mb-4">Just Speak</h3>
                            <p className="text-slate-600">Don't like typing? Just call our number and speak naturally. Cura understands voice perfectly.</p>
                        </div>
                        <div className="p-8 rounded-3xl bg-[#F6F8FC]">
                            <HeartHandshake className="w-10 h-10 text-[#003b82] mb-6" />
                            <h3 className="text-3xl font-['Instrument_Serif'] mb-4">Patient & Kind</h3>
                            <p className="text-slate-600">Cura never gets tired or impatient. Ask as many questions as you need, anytime.</p>
                        </div>
                        <div className="p-8 rounded-3xl bg-[#F6F8FC]">
                            <Bell className="w-10 h-10 text-[#003b82] mb-6" />
                            <h3 className="text-3xl font-['Instrument_Serif'] mb-4">24/7 Support</h3>
                            <p className="text-slate-600">Wake up not feeling well? Cura is awake too. Get peace of mind instantly.</p>
                        </div>
                    </div>

                </div>
            </main>

            <Footer />
        </div>
    );
};

export default SeniorsPage;
